"use client";

import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const inquiryOptions = [{
  value: "brand_strategy",
  label: "Brand Strategy"
}, {
  value: "creative_direction",
  label: "Creative Direction"
}, {
  value: "marketing_audit",
  label: "Marketing Audit"
}, {
  value: "partnership",
  label: "Partnership Inquiry"
}, {
  value: "other",
  label: "Other"
}];
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-start justify-center min-h-[400px] space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-1000">
        <p className="font-serif text-4xl md:text-5xl text-foreground leading-tight max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-foreground/20 text-foreground hover:bg-foreground hover:text-background transition-all duration-500 px-10 py-7 text-[10px] uppercase tracking-[0.3em]">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-16">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16">
        <FormField control={control} name="full_name" rules={{
        required: "Please share your name"
      }} render={({
        field
      }) => <FormItem className="space-y-4">
              <FormLabel className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className="h-14 border-0 border-b border-foreground/20 bg-transparent px-0 rounded-lg focus-visible:ring-0 focus-visible:border-accent transition-all placeholder:text-foreground/20 text-lg" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase tracking-wider text-accent" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "An email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-4">
              <FormLabel className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@company.com" className="h-14 border-0 border-b border-foreground/20 bg-transparent px-0 rounded-lg focus-visible:ring-0 focus-visible:border-accent transition-all placeholder:text-foreground/20 text-lg" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase tracking-wider text-accent" />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please choose an option"
    }} render={({
      field
    }) => <FormItem className="space-y-4">
            <FormLabel className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
              Inquiry Type
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="h-14 w-full border-0 border-b border-foreground/20 bg-transparent px-0 rounded-lg focus-visible:ring-0 focus-visible:border-accent transition-all text-lg">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-lg border-foreground/10 bg-background text-foreground">
                {inquiryOptions.map((option, i) => <SelectItem key={option.value} data-index={i} value={option.value} className="text-sm py-4 focus:bg-accent focus:text-background">
                    {option.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage className="text-[10px] uppercase tracking-wider text-accent" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Share your vision"
    }} render={({
      field
    }) => <FormItem className="space-y-4">
            <FormLabel className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
              Message
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Tell me about your project..." className="min-h-32 border-0 border-b border-foreground/20 bg-transparent px-0 rounded-lg focus-visible:ring-0 focus-visible:border-accent transition-all placeholder:text-foreground/20 resize-none text-lg" {...field} />
            </FormControl>
            <FormMessage className="text-[10px] uppercase tracking-wider text-accent" />
          </FormItem>} />

      {submitError && <div className="py-6 border-b border-accent/20">
          <p className="text-[10px] uppercase tracking-widest text-accent">{submitError}</p>
        </div>}

      <div className="pt-12">
        <Button type="submit" disabled={isSubmitting} className="h-16 w-full md:w-auto bg-accent text-accent-foreground px-16 rounded-lg text-[10px] uppercase tracking-[0.4em] hover:bg-accent/90 transition-all duration-700 shadow-lg">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Sending...
            </> : "Start a Project"}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true
  });
  return <section data-section-id="2019" id="contact" className="relative bg-background py-32 md:py-48 lg:py-64 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-24 lg:gap-32 items-start transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-5 space-y-24">
            <div className="space-y-8">
              <p className="text-[12px] font-semibold uppercase tracking-[0.4em] text-accent">
                Contact
              </p>
              <h2 className="font-sans text-5xl md:text-7xl font-medium leading-[1.1] tracking-tight text-foreground">
                Let's build <br />
                <span className="italic font-serif font-light">something real.</span>
              </h2>
              <div className="max-w-md space-y-10 pt-4">
                <p className="text-lg md:text-xl leading-relaxed text-foreground/70 font-light">
                  You have built the foundation. Now, let's ensure the world sees it. Reach out to discuss your vision, your challenges, and how we can elevate your brand presence.
                </p>
                
                <div className="space-y-8">
                  <div className="group">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-foreground/40 mb-2">Direct Inquiry</p>
                    <Link to="mailto:myles@mylesdacres.com" className="text-xl tracking-wider text-foreground hover:text-accent transition-colors duration-500">
                      myles@mylesdacres.com
                    </Link>
                  </div>
                  <div className="group">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-foreground/40 mb-2">Availability</p>
                    <p className="text-xl tracking-wider text-foreground">
                      Available for select creative consultations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/contact_architectural_minimalism.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/contact_architectural_minimalism.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/contact_architectural_minimalism.webp-wvc-sizes" alt="Minimalist architectural detail with deep shadows" className="w-full aspect-[4/5] object-cover rounded-lg grayscale hover:grayscale-0 transition-all duration-1000" loading="lazy" />
            </div>
          </div>

          {}
          <div className="lg:col-span-7 lg:pl-16">
            <div className="relative">
              <FormProvider formKey="contact_form_redesign" formId="contact-form-main" className="relative" sectionName="contact-section" defaultValues={{
              full_name: "",
              email: "",
              inquiry_type: "",
              message: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              inquiry_type: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Start a Project" successMessage="Message received. I will be in touch shortly." errorMessage="Something went wrong. Please try again.">
                <ReachOutForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}